import { useState, useCallback, useRef, useMemo } from 'react'
import {
  Typography, Tabs, Button, Input, Upload, Space, Tag, message, Table,
  Popconfirm, Empty, Spin, Modal, Dropdown, Alert,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined,
  FileTextOutlined, ThunderboltOutlined,
  EyeOutlined, EditOutlined,
  ApartmentOutlined, TableOutlined, DownOutlined,
} from '@ant-design/icons'
import * as aiApi from '@/api/aiTestCases'
import type { AITestTask, AITestCaseItem, GenerateResult } from '@/api/aiTestCases'
import MindMapView, { type MindMapNode } from '@/components/MindMapView'
import { useThemeStore, themes } from '@/stores/theme'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

const priorityColors: Record<string, string> = {
  P0: 'red', P1: 'orange', P2: 'blue', P3: 'default',
}

const statusConfig: Record<string, { color: string; label: string }> = {
  pending: { color: 'default', label: '等待中' },
  running: { color: 'processing', label: '生成中' },
  completed: { color: 'success', label: '已完成' },
  failed: { color: 'error', label: '失败' },
}

function parseJSON(text: string): unknown {
  try { return JSON.parse(text) } catch { return text }
}

export default function AITestCases() {
  const { isDark, current } = useThemeStore()
  /** 当前主题主色（思维导图分支等 canvas/SVG 场景需真实色值） */
  const themePrimary = (themes.find((t) => t.key === current) || themes[0]).colors.primary
  const [activeTab, setActiveTab] = useState('generate')
  // 生成相关
  const [documentText, setDocumentText] = useState('')
  const [sourceType, setSourceType] = useState('text')
  const [generating, setGenerating] = useState(false)
  const [lastResult, setLastResult] = useState<GenerateResult | null>(null)
  // 历史记录
  const [tasks, setTasks] = useState<AITestTask[]>([])
  const [taskLoading, setTaskLoading] = useState(false)
  const [taskPagination, setTaskPagination] = useState({ page: 1, pageSize: 10, total: 0 })
  // 用例查看弹窗
  const [casesModalOpen, setCasesModalOpen] = useState(false)
  const [casesModalTitle, setCasesModalTitle] = useState('')
  const [cases, setCases] = useState<AITestCaseItem[]>([])
  const [casesLoading, setCasesLoading] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table')
  const [editingCell, setEditingCell] = useState<{ id: number; field: string } | null>(null)
  const [editValue, setEditValue] = useState('')
  const editInputRef = useRef<any>(null)
  // 导出目标 taskId
  const [exportTaskId, setExportTaskId] = useState<number | null>(null)

  // ========== 文档上传 ==========
  const handleUpload = async (file: File) => {
    try {
      const res = await aiApi.parseDocument(file)
      setDocumentText(res.content)
      setSourceType(res.source_type)
      message.success(`文档解析成功，共 ${res.char_count} 个字符`)
    } catch {
      message.error('文档解析失败')
    }
    return false
  }

  // ========== 生成用例 ==========
  const handleGenerate = async () => {
    if (!documentText.trim() || documentText.trim().length < 10) {
      message.warning('请输入至少 10 个字符的需求描述')
      return
    }
    setGenerating(true)
    setLastResult(null)
    try {
      const res = await aiApi.generateTestCases({
        title: 'AI 用例生成',
        document: documentText,
        source_type: sourceType,
      })
      setLastResult(res)
      message.success(`用例生成成功，共 ${res.total_cases} 条`)
      setActiveTab('history')
      // 程序化切换不触发 Tabs onChange，需手动拉取最新历史列表
      fetchTasks(1, taskPagination.pageSize)
    } catch (e) { console.error('[AITestCases] 操作失败:', e) } finally {
      setGenerating(false)
    }
  }

  // ========== 历史记录 ==========
  const fetchTasks = useCallback(async (page = 1, pageSize = 10) => {
    setTaskLoading(true)
    try {
      const res = await aiApi.listTasks({ page, page_size: pageSize })
      setTasks(res.data)
      setTaskPagination({ page: res.page, pageSize: res.page_size, total: res.total })
    } finally {
      setTaskLoading(false)
    }
  }, [])

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    if (key === 'history') {
      // 每次点击历史记录页签都拉取一次最新列表
      fetchTasks(1, taskPagination.pageSize)
    }
  }

  const handleViewCases = async (taskId: number) => {
    setCasesLoading(true)
    setCasesModalOpen(true)
    setViewMode('table')
    try {
      const res = await aiApi.listCases(taskId)
      setCases(res || [])
      setExportTaskId(taskId)
      // 从 tasks 中找到标题
      const task = tasks.find(t => t.id === taskId)
      setCasesModalTitle(task?.title || `任务 #${taskId}`)
    } finally {
      setCasesLoading(false)
    }
  }

  const handleDeleteTask = async (taskId: number) => {
    try {
      await aiApi.deleteTask(taskId)
      message.success('删除成功')
      fetchTasks(taskPagination.page, taskPagination.pageSize)
    } catch (e) { console.error('[AITestCases] 操作失败:', e) }
  }

  // ========== 导出 ==========
  const handleExport = (format: 'excel' | 'xmind') => {
    if (!exportTaskId) return
    const url = format === 'excel'
      ? aiApi.exportExcelUrl(exportTaskId)
      : aiApi.exportXmindUrl(exportTaskId)
    const ext = format === 'excel' ? '.xlsx' : '.xmind'
    aiApi.downloadFile(url, `测试用例${ext}`)
    message.success(`正在下载${format === 'excel' ? 'Excel' : 'Xmind'}文件...`)
  }

  // ========== 行内编辑 ==========
  const startEdit = (record: AITestCaseItem, field: string) => {
    const value = (record as any)[field] || ''
    setEditingCell({ id: record.id, field })
    setEditValue(value)
    setTimeout(() => editInputRef.current?.focus(), 100)
  }

  const saveEdit = async (record: AITestCaseItem) => {
    if (!editingCell) return
    try {
      await aiApi.updateCase(record.id, { [editingCell.field]: editValue })
      setCases((prev) =>
        prev.map((c) =>
          c.id === record.id ? { ...c, [editingCell.field]: editValue } : c,
        ),
      )
      message.success('修改已保存')
    } catch (e) { console.error('[AITestCases] 操作失败:', e) } finally {
      setEditingCell(null)
    }
  }

  // ========== 思维导图数据 ==========
  const mindMapData = useMemo((): MindMapNode | null => {
    if (cases.length === 0) return null

    const priorityBranchColors: Record<string, string> = {
      P0: '#ef4444', P1: '#f59e0b', P2: themePrimary, P3: '#a8b0bf',
    }
    const palette = ['#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#f97316']

    // 按模块分组
    const modules: Record<string, Record<string, AITestCaseItem[]>> = {}
    cases.forEach((c) => {
      const m = c.module || '默认模块'
      const s = c.sub_module || '默认子模块'
      if (!modules[m]) modules[m] = {}
      if (!modules[m][s]) modules[m][s] = []
      modules[m][s].push(c)
    })

    let modIdx = 0
    const moduleNodes: MindMapNode[] = Object.entries(modules).map(([module, subModules]) => {
      const modColor = palette[modIdx % palette.length]
      modIdx++
      const subNodes: MindMapNode[] = Object.entries(subModules).map(([sub, subCases]) => {
        const caseNodes: MindMapNode[] = subCases.map((c) => {
          const steps = parseJSON(c.steps)
          const expected = parseJSON(c.expected)
          const precond = parseJSON(c.precondition)

          const stepsStr = Array.isArray(steps)
            ? steps.map((s: { step: number; action: string; data?: string }) => `${s.step}. ${s.action}${s.data ? ` [${s.data}]` : ''}`).join('\n')
            : String(steps || '')
          const expectedStr = Array.isArray(expected) ? expected.join('\n') : String(expected || '')
          const precondStr = Array.isArray(precond) ? precond.join('\n') : String(precond || '')

          const detailChildren: MindMapNode[] = [
            { id: `${c.id}-method`, topic: `方法: ${c.design_method || '-'}` },
          ]
          if (precondStr) {
            detailChildren.splice(0, 0, { id: `${c.id}-pre`, topic: `前置: ${precondStr}` })
          }
          detailChildren.push(
            { id: `${c.id}-step`, topic: `步骤:\n${stepsStr}` },
            { id: `${c.id}-exp`, topic: `预期:\n${expectedStr}` },
          )

          return {
            id: `${c.id}`,
            topic: `[${c.priority}] ${c.title}`,
            branchColor: priorityBranchColors[c.priority] || '#a8b0bf',
            tags: [c.priority],
            children: detailChildren,
          }
        })
        return {
          id: `${module}-${sub}`,
          topic: `${sub} (${subCases.length})`,
          branchColor: modColor,
          children: caseNodes,
        }
      })
      return {
        id: module,
        topic: `${module} (${subNodes.length}个子模块)`,
        branchColor: modColor,
        children: subNodes,
      }
    })

    return {
      id: 'root',
      topic: casesModalTitle || '测试用例',
      expanded: true,
      children: moduleNodes,
    }
  }, [cases, casesModalTitle, themePrimary])

  // ========== 表格列 ==========
  const taskColumns: ColumnsType<AITestTask> = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '任务标题', dataIndex: 'title', width: 150, ellipsis: true },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (s: string) => {
        const cfg = statusConfig[s] || { color: 'default', label: s }
        return <Tag color={cfg.color}>{cfg.label}</Tag>
      },
    },
    { title: '模型', dataIndex: 'model_name', width: 130, ellipsis: true },
    { title: '用例数', dataIndex: 'total_cases', width: 70 },
    {
      title: '创建时间', dataIndex: 'create_time', width: 160,
      render: (v: string) => v?.split('T')[0] + ' ' + v?.split('T')[1]?.split('.')[0],
    },
    {
      title: '操作', width: 220, fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          {record.status === 'completed' && record.total_cases > 0 && (
            <>
              <Button size="small" type="link"
                onClick={() => handleViewCases(record.id)}>查看用例</Button>
              <Dropdown menu={{
                items: [
                  { key: 'excel', label: '导出 Excel', icon: <TableOutlined />, onClick: () => { setExportTaskId(record.id); handleExport('excel') } },
                  { key: 'xmind', label: '导出 Xmind', icon: <ApartmentOutlined />, onClick: () => { setExportTaskId(record.id); handleExport('xmind') } },
                ],
              }}>
                <Button size="small" type="link">
                  导出 <DownOutlined />
                </Button>
              </Dropdown>
            </>
          )}
          <Popconfirm
            title="确定删除该任务？"
            onConfirm={() => handleDeleteTask(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger type="link">删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const caseColumns: ColumnsType<AITestCaseItem> = [
    { title: '编号', dataIndex: 'case_id', width: 130 },
    { title: '模块', dataIndex: 'module', width: 100 },
    {
      title: '标题', dataIndex: 'title', width: 280, ellipsis: true,
      render: (text: string, record) => (
        editingCell?.id === record.id && editingCell?.field === 'title' ? (
          <Input ref={editInputRef} value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => saveEdit(record)}
            onPressEnter={() => saveEdit(record)}
            size="small" />
        ) : (
          <span onDoubleClick={() => startEdit(record, 'title')} style={{ cursor: 'pointer' }}>
            {text}
          </span>
        )
      ),
    },
    {
      title: '优先级', dataIndex: 'priority', width: 70,
      render: (v: string) => <Tag color={priorityColors[v]}>{v}</Tag>,
    },
    {
      title: '设计方法', dataIndex: 'design_method', width: 100, render: (v: string) => v || '-',
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          AI 用例生成
        </Title>
      </div>

      <div className="content-card">
        <Tabs activeKey={activeTab} onChange={handleTabChange}
          items={[
            {
              key: 'generate',
              label: <span><ThunderboltOutlined /> 生成用例</span>,
              children: (
                /* fit 布局下内容区弹性填充，超出时内部滚动（tabpane 已是纵向 flex） */
                <div style={{ padding: '16px 22px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <Alert
                    message="输入需求文档内容，AI 将自动分析需求并生成结构化测试用例"
                    type="info" showIcon style={{ marginBottom: 16 }}
                  />
                  <div style={{ marginBottom: 16 }}>
                    <Space>
                      <Upload accept=".md,.txt,.docx" maxCount={1}
                        beforeUpload={handleUpload} showUploadList={false}>
                        <Button>上传需求文档</Button>
                      </Upload>
                      <Text type="secondary">支持 .md / .txt / .docx 格式</Text>
                      {sourceType !== 'text' && (
                        <Tag color="blue">{sourceType === 'markdown' ? 'Markdown' : 'Word'}</Tag>
                      )}
                    </Space>
                  </div>

                  <TextArea
                    value={documentText}
                    onChange={(e) => setDocumentText(e.target.value)}
                    placeholder="请在此粘贴需求文档内容，或点击上方按钮上传文件..."
                    rows={14}
                    style={{ fontFamily: 'monospace', fontSize: 13, marginBottom: 16 }}
                  />

                  <Space>
                    <Button type="primary" size="large"
                      onClick={handleGenerate} loading={generating}>
                      {generating ? 'AI 正在生成用例...' : '开始生成用例'}
                    </Button>
                    <Button onClick={() => setDocumentText('')}>清空</Button>
                  </Space>

                  {generating && (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <Spin size="large" />
                      <Paragraph type="secondary" style={{ marginTop: 16 }}>
                        AI 正在分析需求文档、设计测试用例并进行自评优化，请耐心等待...
                      </Paragraph>
                    </div>
                  )}

                  {lastResult && (
                    <Alert type="success"
                      message={`生成完成！共生成 ${lastResult.total_cases} 条用例`}
                      description={
                        <Button type="link" onClick={() => handleTabChange('history')}
                          style={{ padding: 0 }}>点击查看历史记录 →</Button>
                      }
                      style={{ marginTop: 16 }} showIcon />
                  )}
                </div>
              ),
            },
            {
              key: 'history',
              label: <span><FileTextOutlined /> 历史记录</span>,
              children: (
                <>
                  <Table
                    columns={taskColumns}
                    dataSource={tasks}
                    rowKey="id"
                    loading={taskLoading}
                    scroll={{ y: '100%' }} /* 表体高度由 aurora-page--fit 的 flex 布局分配，列表铺满页面 */
                    pagination={{
                      current: taskPagination.page,
                      pageSize: taskPagination.pageSize,
                      total: taskPagination.total,
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 条`,
                    }}
                    onChange={(pagination: { current?: number; pageSize?: number }) => fetchTasks(pagination.current, pagination.pageSize)}
                    locale={{ emptyText: <Empty description="暂无生成记录" /> }}
                  />
                </>
              ),
            },
          ]}
        />
      </div>

      {/* 查看用例弹窗 */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>用例详情 - {casesModalTitle}</span>
            {exportTaskId && (
              <Dropdown menu={{
                items: [
                  { key: 'excel', label: '导出 Excel', icon: <TableOutlined />, onClick: () => handleExport('excel') },
                  { key: 'xmind', label: '导出 Xmind', icon: <ApartmentOutlined />, onClick: () => handleExport('xmind') },
                ],
              }}>
                <Button size="small">导出 <DownOutlined /></Button>
              </Dropdown>
            )}
          </Space>
        }
        open={casesModalOpen}
        onCancel={() => { setCasesModalOpen(false); setCases([]) }}
        width={1200}
        footer={null}
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
        destroyOnClose
      >
        <Tabs activeKey={viewMode} onChange={(k) => setViewMode(k as 'table' | 'tree')}
          style={{ marginTop: -8 }}
          items={[
            {
              key: 'table',
              label: <span><TableOutlined /> 表格视图</span>,
              children: casesLoading ? (
                <Spin tip="加载用例中..." style={{ display: 'block', padding: 60 }} />
              ) : (
                <Table
                  columns={caseColumns}
                  dataSource={cases}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  expandable={{
                    expandedRowRender: (record: AITestCaseItem) => {
                      const steps = parseJSON(record.steps)
                      const expected = parseJSON(record.expected)
                      const precond = parseJSON(record.precondition)
                      return (
                        <div style={{ padding: '8px 16px', background: isDark ? 'var(--bg)' : '#fafbfc', borderRadius: 6 }}>
                          {Array.isArray(precond) && precond.length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                              <Text strong>前置条件：</Text>
                              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                                {precond.map((p: string, i: number) => <li key={`precond-${i}-${p.substring(0, 10)}`}>{p}</li>)}
                              </ul>
                            </div>
                          )}
                          <div style={{ marginBottom: 8 }}>
                            <Text strong>测试步骤：</Text>
                            <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                              {Array.isArray(steps) && steps.map((s: { step: number; action: string; data?: string }, i: number) => (
                                <li key={`step-${i}-${s.action?.substring(0, 10)}`}>
                                  {s.action}
                                  {s.data ? <code style={{ marginLeft: 8, fontSize: 12, color: '#06b6d4' }}>[{s.data}]</code> : null}
                                </li>
                              ))}
                            </ol>
                          </div>
                          {Array.isArray(expected) && expected.length > 0 && (
                            <div>
                              <Text strong>预期结果：</Text>
                              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                                {expected.map((e: string, i: number) => <li key={`exp-${i}-${e.substring(0, 10)}`}>{e}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )
                    },
                    rowExpandable: () => true,
                  }}
                  scroll={{ x: 800 }}
                  style={{ maxHeight: '60vh', overflow: 'auto' }}
                />
              ),
            },
            {
              key: 'tree',
              label: <span><ApartmentOutlined /> 思维导图</span>,
              children: casesLoading ? (
                <Spin tip="加载中..." style={{ display: 'block', padding: 60 }} />
              ) : !mindMapData ? (
                <Empty description="暂无用例" style={{ padding: 40 }} />
              ) : (
                <MindMapView data={mindMapData} height={600} readonly />
              ),
            },
          ]}
        />
      </Modal>
    </div>
  )
}
